import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getAppPortalRoot } from "../utils/appPortal";
import { X } from "lucide-react";
import liqueurMetamorpheImg from "../assets/liqueurDuMetamorphe.png";
import { useCustomScrollbar } from "../hooks/useCustomScrollbar";
import {
  formatMetamorphCandidateHint,
  stripTrainingSessionDuration,
} from "../utils/compactQuestVariantLabels";
import {
  applyMetamorph,
  debitMetamorphPotion,
  fetchMetamorphCandidates,
} from "../utils/questApi";
import "./DeleteWordModal.css";
import "./QuestMetamorphModal.css";

const OBJECTIVE_BLOCK_REASONS = {
  completed: "Cet objectif est accompli",
  failed: "Objectif clos",
  "in-progress": "Cet objectif est entamé",
  "no-candidate": "Aucun remplacement possible",
};

const UNAVAILABLE_MESSAGES = {
  expired: "La quête du jour est terminée : la liqueur attendra la prochaine quête.",
  "already-used":
    "La Liqueur du Métamorphe a déjà été utilisée pour cette quête. Elle sera de nouveau utilisable avec la prochaine quête.",
  "no-potion":
    "Vous ne possédez aucune Liqueur du Métamorphe. Vous pouvez en obtenir dans le catalogue des potions.",
  "no-replaceable-objective":
    "Tous les objectifs sont accomplis ou déjà entamés : aucun ne peut être remplacé.",
};

function OptionList({ items, ariaLabel, renderItem }) {
  return (
    <ul className="quest-metamorph-modal__list" role="radiogroup" aria-label={ariaLabel}>
      {items.map(renderItem)}
    </ul>
  );
}

function MetamorphScrollColumn({ children, dependency }) {
  const scrollRef = useRef(null);
  const {
    trackRef,
    scrollbar,
    updateScrollbar,
    handleThumbPointerDown,
    handleTrackPointerDown,
  } = useCustomScrollbar(scrollRef);
  const [scrollFades, setScrollFades] = useState({ top: false, bottom: false });

  const updateScrollUi = useCallback(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollEl;
    const threshold = 4;
    const hasOverflow = scrollHeight > clientHeight + 1;

    setScrollFades({
      top: hasOverflow && scrollTop > threshold,
      bottom: hasOverflow && scrollTop + clientHeight < scrollHeight - threshold,
    });
    updateScrollbar();
  }, [updateScrollbar]);

  useEffect(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) return undefined;

    updateScrollUi();
    const observer = new ResizeObserver(() => updateScrollUi());
    observer.observe(scrollEl);
    if (scrollEl.firstElementChild) observer.observe(scrollEl.firstElementChild);

    return () => observer.disconnect();
  }, [dependency, updateScrollUi]);

  return (
    <div className="quest-metamorph-modal__column-viewport">
      <div className="quest-metamorph-modal__column-scroller">
        <div
          ref={scrollRef}
          className="quest-metamorph-modal__column-scroll"
          onScroll={updateScrollUi}
        >
          {children}
        </div>
        <div
          ref={trackRef}
          className={[
            "quest-metamorph-modal__scrollbar",
            !scrollbar.visible && "quest-metamorph-modal__scrollbar--hidden",
          ]
            .filter(Boolean)
            .join(" ")}
          aria-hidden="true"
          onPointerDown={handleTrackPointerDown}
        >
          {scrollbar.visible ? (
            <div
              className="quest-metamorph-modal__scrollbar-thumb"
              style={{
                height: `${scrollbar.height}px`,
                transform: `translateY(${scrollbar.top}px)`,
              }}
              onPointerDown={handleThumbPointerDown}
            />
          ) : null}
        </div>
      </div>
      {scrollFades.top ? (
        <div className="quest-metamorph-modal__fade quest-metamorph-modal__fade--top" aria-hidden="true" />
      ) : null}
      {scrollFades.bottom ? (
        <div
          className="quest-metamorph-modal__fade quest-metamorph-modal__fade--bottom"
          aria-hidden="true"
        />
      ) : null}
    </div>
  );
}

export default function QuestMetamorphModal({ open, date, onClose, onApplied }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [overview, setOverview] = useState(null);
  const [selectedObjectiveId, setSelectedObjectiveId] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const loadOverview = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const payload = await fetchMetamorphCandidates();
      setOverview(payload);
    } catch (err) {
      setError(err.message || "Impossible de préparer la Liqueur du Métamorphe.");
      setOverview(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setSelectedObjectiveId("");
    setSelectedCategoryId("");
    setResult(null);
    loadOverview();
  }, [open, loadOverview]);

  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !submitting) onClose?.();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, submitting, onClose]);

  const objectives = useMemo(
    () => (Array.isArray(overview?.objectives) ? overview.objectives : []),
    [overview]
  );
  const selectedObjective = useMemo(
    () => objectives.find((entry) => entry.id === selectedObjectiveId) || null,
    [objectives, selectedObjectiveId]
  );
  const candidates = selectedObjective?.candidates || [];
  const selectedCandidate =
    candidates.find((entry) => entry.categoryId === selectedCategoryId) || null;
  const potionCount = Number(overview?.potionCount) || 0;
  const available = Boolean(overview?.available);
  const unavailableMessage =
    overview && !available
      ? UNAVAILABLE_MESSAGES[overview.unavailableReason] ||
        "La Liqueur du Métamorphe n'est pas utilisable pour le moment."
      : "";

  const handleSelectObjective = (objectiveId) => {
    if (submitting) return;
    setSelectedObjectiveId(objectiveId);
    setSelectedCategoryId("");
  };

  const handleApply = async () => {
    if (!selectedObjective || !selectedCandidate || submitting) return;

    setSubmitting(true);
    setError("");
    try {
      const applied = await applyMetamorph(selectedObjective.id, selectedCandidate.categoryId);

      try {
        await debitMetamorphPotion(applied.date || date, applied.debitToken);
      } catch (debitErr) {
        console.error("Débit de la Liqueur du Métamorphe :", debitErr);
      }

      setResult({
        replacedLabel: stripTrainingSessionDuration(
          applied.replacedObjective?.label || selectedObjective.label
        ),
        newLabel: stripTrainingSessionDuration(applied.newObjective?.label || ""),
      });
      if (applied.payload) {
        await onApplied?.(applied.payload);
      }
    } catch (err) {
      setError(err.message || "Impossible de remplacer cet objectif.");
      if (err.code === "already-used" || err.code === "conflict" || err.code === "in-progress") {
        loadOverview();
        setSelectedObjectiveId("");
        setSelectedCategoryId("");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const showChooser = !loading && !error && available && !result;
  const showCloseOnly = Boolean(result) || (!loading && !available && !error);

  return createPortal(
    <div
      className="delete-word-modal-backdrop"
      onMouseDown={() => {
        if (!submitting) onClose?.();
      }}
    >
      <div
        className="delete-word-modal quest-metamorph-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="quest-metamorph-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="delete-word-modal__close"
          onClick={onClose}
          disabled={submitting}
          aria-label="Fermer"
        >
          <X size={18} strokeWidth={2} />
        </button>

        <div className="quest-metamorph-modal__header">
          <h2 id="quest-metamorph-title" className="quest-metamorph-modal__title">
            Liqueur du Métamorphe
          </h2>
          <img
            src={liqueurMetamorpheImg}
            alt=""
            aria-hidden="true"
            decoding="sync"
            draggable={false}
            className="quest-metamorph-modal__potion"
          />
          <div className="quest-metamorph-modal__heading">
            <p className="quest-metamorph-modal__stock">
              Vous en possédez <strong>{overview ? potionCount : "—"}</strong>
            </p>
            <p className="quest-metamorph-modal__subtitle">
              Cette potion remplace un objectif qui n&apos;a pas encore été commencé par un
              objectif au choix. Vous ne décidez pas du niveau d&apos;exigence qui, lui, sera
              choisi aléatoirement. Vous ne pouvez utiliser cette potion qu&apos;une seule fois
              par quête.
            </p>
          </div>
        </div>

        <div className="quest-metamorph-modal__body">
          {showChooser ? (
            <div className="quest-metamorph-modal__columns">
              <section className="quest-metamorph-modal__column">
                <h3 className="quest-metamorph-modal__step-title">
                  <span className="quest-metamorph-modal__step-index">1</span>
                  Objectif à remplacer
                </h3>
                <MetamorphScrollColumn dependency={objectives.map((item) => item.id).join("|")}>
                  <OptionList
                    items={objectives}
                    ariaLabel="Objectif à remplacer"
                    renderItem={(objective) => {
                      const disabled = !objective.replaceable;
                      const checked = objective.id === selectedObjectiveId;
                      return (
                        <li key={objective.id}>
                          <button
                            type="button"
                            role="radio"
                            aria-checked={checked}
                            disabled={disabled || submitting}
                            className={[
                              "quest-metamorph-modal__option",
                              checked && "quest-metamorph-modal__option--selected",
                              disabled && "quest-metamorph-modal__option--disabled",
                            ]
                              .filter(Boolean)
                              .join(" ")}
                            onClick={() => handleSelectObjective(objective.id)}
                          >
                            <span className="quest-metamorph-modal__option-label">
                              {stripTrainingSessionDuration(objective.label)}
                            </span>
                            {disabled ? (
                              <span className="quest-metamorph-modal__option-note">
                                {OBJECTIVE_BLOCK_REASONS[objective.reason] || "Non remplaçable"}
                              </span>
                            ) : null}
                          </button>
                        </li>
                      );
                    }}
                  />
                </MetamorphScrollColumn>
              </section>

              <section
                className={`quest-metamorph-modal__column${
                  selectedObjective ? "" : " quest-metamorph-modal__column--pending"
                }`}
              >
                <h3 className="quest-metamorph-modal__step-title">
                  <span className="quest-metamorph-modal__step-index">2</span>
                  Nouvel objectif à atteindre
                </h3>
                <MetamorphScrollColumn
                  dependency={`${selectedObjectiveId}|${candidates
                    .map((item) => item.categoryId)
                    .join("|")}`}
                >
                  {selectedObjective ? (
                    <OptionList
                      items={candidates}
                      ariaLabel="Nouvel objectif à atteindre"
                      renderItem={(candidate) => {
                        const checked = candidate.categoryId === selectedCategoryId;
                        return (
                          <li key={candidate.categoryId}>
                            <button
                              type="button"
                              role="radio"
                              aria-checked={checked}
                              disabled={submitting}
                              className={[
                                "quest-metamorph-modal__option",
                                checked && "quest-metamorph-modal__option--selected",
                              ]
                                .filter(Boolean)
                                .join(" ")}
                              onClick={() => setSelectedCategoryId(candidate.categoryId)}
                            >
                            <span className="quest-metamorph-modal__option-label">
                              {formatMetamorphCandidateHint(candidate.variantLabels) ||
                                candidate.title}
                            </span>
                            </button>
                          </li>
                        );
                      }}
                    />
                  ) : (
                    <p className="quest-metamorph-modal__hint">
                      Choisissez d'abord l'objectif que vous souhaitez remplacer, dans la
                      liste de gauche.
                    </p>
                  )}
                </MetamorphScrollColumn>
              </section>
            </div>
          ) : (
            <div className="quest-metamorph-modal__message">
              {loading ? (
                <p className="quest-metamorph-modal__status">Préparation de la liqueur…</p>
              ) : null}
              {error ? (
                <p className="quest-metamorph-modal__status quest-metamorph-modal__status--error">
                  {error}
                </p>
              ) : null}
              {!loading && !error && !available && !result ? (
                <p className="quest-metamorph-modal__status">{unavailableMessage}</p>
              ) : null}
              {result ? (
                <div className="quest-metamorph-modal__result">
                  <p className="quest-metamorph-modal__result-title">Métamorphose accomplie</p>
                  <p className="quest-metamorph-modal__result-line">
                    <span className="quest-metamorph-modal__result-old">{result.replacedLabel}</span>
                  </p>
                  <p className="quest-metamorph-modal__result-arrow" aria-hidden="true">
                    ↓
                  </p>
                  <p className="quest-metamorph-modal__result-line">
                    <strong>{result.newLabel}</strong>
                  </p>
                </div>
              ) : null}
            </div>
          )}
        </div>

        <div className="quest-metamorph-modal__actions">
          {showCloseOnly ? (
            <button
              type="button"
              className="quest-metamorph-modal__btn quest-metamorph-modal__btn--apply"
              onClick={onClose}
            >
              Fermer
            </button>
          ) : (
            <>
              <button
                type="button"
                className="quest-metamorph-modal__btn quest-metamorph-modal__btn--cancel"
                onClick={onClose}
                disabled={submitting}
              >
                Annuler
              </button>
              <button
                type="button"
                className="quest-metamorph-modal__btn quest-metamorph-modal__btn--apply"
                onClick={handleApply}
                disabled={!selectedObjective || !selectedCandidate || submitting || loading}
              >
                {submitting ? "Métamorphose…" : "Utiliser"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    getAppPortalRoot()
  );
}
